import type { JsonSchema } from "./schema"

// Lazy load format libraries to reduce initial bundle size
const loadYaml = async () => {
  const YAML = await import("yaml")
  return YAML.default
}

const loadToml = async () => {
  const TOML = await import("@iarna/toml")
  return TOML.default
}

const loadToon = async () => {
  const { encode } = await import("@toon-format/toon")
  return encode
}

const loadPapa = async () => {
  const Papa = await import("papaparse")
  return Papa.default
}

export const toYaml = async (data: JsonSchema): Promise<string> => {
  try {
    const YAML = await loadYaml()
    return YAML.stringify(data)
  } catch (error) {
    return "Error converting to YAML: " + (error as Error).message
  }
}

export const toToml = async (data: JsonSchema): Promise<string> => {
  try {
    const TOML = await loadToml()
    if (Array.isArray(data)) {
      return TOML.stringify({ root: data })
    }
    return TOML.stringify(data)
  } catch (e) {
    return "Error converting to TOML: " + (e as Error).message
  }
}

export const toToon = async (data: JsonSchema): Promise<string> => {
  try {
    const encode = await loadToon()
    return encode(data)
  } catch (e) {
    return "Error converting to TOON: " + (e as Error).message
  }
}

const flattenObject = (obj: JsonSchema, prefix = ""): JsonSchema => {
  return Object.keys(obj).reduce((acc: JsonSchema, k: string) => {
    const pre = prefix.length ? prefix + "." : ""
    if (
      typeof obj[k] === "object" &&
      obj[k] !== null &&
      !Array.isArray(obj[k])
    ) {
      Object.assign(acc, flattenObject(obj[k], pre + k))
    } else if (Array.isArray(obj[k])) {
      acc[pre + k] = obj[k]
        .map((item: JsonSchema) =>
          typeof item === "object" && item !== null
            ? JSON.stringify(item)
            : item
        )
        .join("|")
    } else {
      acc[pre + k] = obj[k]
    }
    return acc
  }, {})
}

export const toCsv = async (data: JsonSchema): Promise<string> => {
  try {
    const Papa = await loadPapa()
    const input = Array.isArray(data) ? data : [data]
    const flattened = input.map((item: JsonSchema) => flattenObject(item))
    return Papa.unparse(flattened)
  } catch {
    return "Error converting to CSV"
  }
}

export const toMinifiedJson = (data: JsonSchema): string => {
  return JSON.stringify(data)
}
